import { useEffect, useRef } from 'react';
import { api } from '@/api/client';

/**
 * Hook to sync GHL location context with backend session
 * Ensures session location_id matches current GHL location
 */
export function useLocationSync() {
  const lastLocationRef = useRef(null);
  const syncingRef = useRef(false);

  useEffect(() => {
    const isInIframe = window.self !== window.top;
    if (!isInIframe) {
      console.log('[LocationSync] Not in iframe, skipping');
      return;
    }

    const syncLocation = async () => {
      if (syncingRef.current) return;
      syncingRef.current = true;

      try {
        console.log('[LocationSync] Requesting current location from GHL...');

        // Request user data from GHL parent
        const ghlData = await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            console.warn('[LocationSync] Timeout waiting for GHL data');
            reject(new Error('Timeout'));
          }, 5000);

          const messageHandler = (event) => {
            // No strict origin allowlist - supports white-labeled GHL instances
            // Real security is handled via SSO decryption on backend
            const data = event.data;
            if (data?.type === 'REQUEST_USER_DATA_RESPONSE' && data.payload?.data) {
              clearTimeout(timeout);
              window.removeEventListener('message', messageHandler);
              resolve(data.payload.data);
            }
          };

          window.addEventListener('message', messageHandler);
          window.parent.postMessage({ type: 'REQUEST_USER_DATA', version: '1.0' }, '*');
        });

        // Decrypt SSO to update session with current location
        const ssoResponse = await api.post('/auth/decrypt-sso', { encryptedData: ghlData });
        
        // Store session_id if provided (for iframe context)
        if (ssoResponse.data.session_id) {
          localStorage.setItem('pos_session_id', ssoResponse.data.session_id);
          console.log('[LocationSync] Updated session_id in localStorage');
        }
        
        // Get updated session to verify
        const { data: session } = await api.get('/auth/me');
        const currentLocation = session.location_id;

        console.log('[LocationSync] Current location:', currentLocation);

        // If location changed, reload page to refresh all data
        if (lastLocationRef.current && lastLocationRef.current !== currentLocation) {
          console.log('[LocationSync] Location changed! Reloading...');
          console.log('  From:', lastLocationRef.current);
          console.log('  To:', currentLocation);
          window.location.reload();
        }

        lastLocationRef.current = currentLocation;
      } catch (error) {
        console.error('[LocationSync] Sync failed:', error);
      } finally {
        syncingRef.current = false;
      }
    };

    // Sync on mount
    syncLocation();

    // Sync when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('[LocationSync] Page visible, syncing location...');
        syncLocation();
      }
    };

    // Sync when window gains focus
    const handleFocus = () => {
      console.log('[LocationSync] Window focused, syncing location...');
      syncLocation();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
}
